import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle, User, FileText, CheckCircle2, Settings, Shield, DollarSign } from "lucide-react";
import { formatDistanceToNow, Locale } from "date-fns";
import { zhTW, enUS, ja, ko, zhCN } from "date-fns/locale";

interface AuditLog {
  id: string;
  admin_id: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
  admin?: {
    user_id: string;
    profiles?: {
      name: string | null;
      email: string;
    };
  };
}

export function RecentActivity() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language.startsWith('en');
  
  const getDateLocale = () => {
    const localeMap: Record<string, Locale> = {
      'zh-TW': zhTW,
      'zh-CN': zhCN,
      'ja': ja,
      'ko': ko,
      'en': enUS
    };
    return localeMap[i18n.language] || enUS;
  };
  
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['admin-audit-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_audit_logs')
        .select(`
          *,
          admin:admins(
            user_id,
            profiles:profiles(name, email)
          )
        `)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return (data || []) as unknown as AuditLog[];
    }
  });

  const getIcon = (action: string) => {
    const lowerAction = action.toLowerCase();
    if (lowerAction.includes('user')) return <User className="h-5 w-5" />;
    if (lowerAction.includes('ticket')) return <AlertCircle className="h-5 w-5" />;
    if (lowerAction.includes('content')) return <FileText className="h-5 w-5" />;
    if (lowerAction.includes('setting')) return <Settings className="h-5 w-5" />;
    if (lowerAction.includes('permission') || lowerAction.includes('role')) return <Shield className="h-5 w-5" />;
    if (lowerAction.includes('payment') || lowerAction.includes('subscription')) return <DollarSign className="h-5 w-5" />;
    return <CheckCircle2 className="h-5 w-5" />;
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      'create_user': t('admin.dashboard.activity.createUser'),
      'update_user': t('admin.dashboard.activity.updateUser'),
      'delete_user': t('admin.dashboard.activity.deleteUser'),
      'create_ticket': t('admin.dashboard.activity.createTicket'),
      'update_ticket': t('admin.dashboard.activity.updateTicket'),
      'close_ticket': t('admin.dashboard.activity.closeTicket'),
      'update_settings': t('admin.dashboard.activity.updateSettings'),
      'login': t('admin.dashboard.activity.adminLogin'),
      'content.delete': t('admin.dashboard.activity.deleteContent'),
      'content.hide': t('admin.dashboard.activity.hideContent'),
      'content.show': t('admin.dashboard.activity.showContent'),
      'content.flag': t('admin.dashboard.activity.flagContent'),
      'content.unflag': t('admin.dashboard.activity.unflagContent'),
      'user.ban': t('admin.dashboard.activity.banUser'),
      'user.unban': t('admin.dashboard.activity.unbanUser'),
      'ticket.reply': t('admin.dashboard.activity.replyTicket'),
      'ticket.assign': t('admin.dashboard.activity.assignTicket'),
      'ticket.close': t('admin.dashboard.activity.closeTicket'),
      'ticket.update': t('admin.dashboard.activity.updateTicket'),
      'promo.create': t('admin.dashboard.activity.createPromo'),
      'promo.update': t('admin.dashboard.activity.updatePromo'),
      'promo.deactivate': t('admin.dashboard.activity.deactivatePromo'),
      'promo.activate': t('admin.dashboard.activity.activatePromo'),
      'user.suspend': t('admin.dashboard.activity.suspendUser'),
      'user.activate': t('admin.dashboard.activity.activateUser'),
      'user.update_subscription': t('admin.dashboard.activity.updateSubscription'),
      'admin.create': t('admin.dashboard.activity.createAdmin'),
      'admin.update': t('admin.dashboard.activity.updateAdmin'),
      'admin.delete': t('admin.dashboard.activity.deleteAdmin'),
    };
    // Try exact match first, then check for partial match
    if (labels[action]) return labels[action];
    
    // Fallback: make display-friendly
    const formatted = action.replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return formatted;
  };

  const getAdminName = (log: AuditLog) => {
    if (log.admin?.profiles?.name) return log.admin.profiles.name;
    if (log.admin?.profiles?.email) return log.admin.profiles.email.split('@')[0];
    return t('admin.dashboard.activity.system');
  };

  return (
    <div className="flex h-full flex-col rounded-xl border-2 border-black bg-white p-8 shadow-[4px_4px_0_#000]">
      <div className="mb-8 flex items-center justify-between">
        <h3 className="text-2xl font-black tracking-tight">{t('admin.dashboard.activity.title')}</h3>
      </div>
      
      <div className="flex-1 overflow-y-auto overflow-x-visible pl-1 pr-2 space-y-4">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : logs.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">{t('admin.dashboard.activity.noActivity')}</div>
        ) : (
          logs.map((log) => {
            const title = log.details ? (log.details as Record<string, unknown>).title as string | undefined : undefined;
            
            return (
              <div key={log.id} className="rounded-xl border-2 border-black bg-gray-50 p-4 shadow-[2px_2px_0_#1A1A1A] hover:shadow-[3px_3px_0_#1A1A1A] transition-shadow">
                {/* Top Row: Icon, Action, Timestamp (right-aligned) */}
                <div className={`flex items-center gap-3 min-w-0 ${isEnglish ? 'pr-16' : ''}`}>
                  {/* Icon */}
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-black bg-white">
                    {getIcon(log.action)}
                  </div>
                  
                  {/* Action Label */}
                  <span className="font-bold text-gray-900 truncate shrink-0">
                    {getActionLabel(log.action)}
                  </span>
                  
                  {/* Spacer divider */}
                  <div className="h-px bg-gray-200 flex-1 min-w-[8px]" />
                  
                  {/* Timestamp - Right aligned */}
                  <span className={`text-gray-400 whitespace-nowrap ${isEnglish ? 'text-[10px] mr-12' : 'text-xs'}`}>
                    {formatDistanceToNow(new Date(log.created_at), { addSuffix: true, locale: getDateLocale() })}
                  </span>
                </div>
                
                {/* Bottom Row: Title + Admin Badge */}
                <div className="mt-2 pl-12 flex items-center justify-between gap-4">
                  <span className="text-sm text-gray-600 truncate flex-1">
                    {title ? (
                      <span className="font-medium text-gray-800">
                        {t('admin.dashboard.activity.targetFormat', { 
                          label: t('admin.dashboard.activity.target'), 
                          title 
                        })}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </span>
                  
                  {/* Admin Name Badge - Bottom right */}
                  <span className="shrink-0 rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-xs font-medium text-gray-600 truncate max-w-[200px]">
                    {getAdminName(log)}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
